import React from "react";
import styled from "styled-components";
import MainLayout from "../layouts/main";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-top: 3rem;
  margin-bottom: 1rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const List = styled.ul`
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const ContactInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ContactInfoItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const StyledWrapper = styled("div")`
  padding-top: 140px;
  min-height: 100vh;
`;

const StyledContainer = styled("div")`
  margin: 0 auto;
  max-width: 1440px;
  width: 90%;
`;

const PrivacyPolicy: React.FC = () => {
  return (
    <MainLayout bg="/images/register-bg.webp">
      <StyledWrapper>
        <StyledContainer>
          <Container>
            <Title>Privacy Policy</Title>
            <Paragraph>
              The Pro Hub application (the "Platform") is a mobile application
              made available and owned by The Up Rank Company Limited
              (collectively referred to as "Company", "we", "us", "our").
            </Paragraph>
            <Paragraph>
              We take the protection of Personal Data (as defined below)
              seriously. This Privacy Policy (the "Privacy Policy") describes
              how the Company collects, uses and discloses the Personal Data of
              the users on the Platform ("Users", "you" or "your") and informs
              them of their data protection rights under the applicable data
              protection laws.
            </Paragraph>
            <SectionTitle>1. WHAT DATA DO WE COLLECT?</SectionTitle>
            <Paragraph>
              "Personal Data" means any data that relates to the User, which can
              directly or indirectly identify the User (excluding the data of
              deceased persons). "Sensitive Personal Data" means any Personal
              Data pertaining to race, ethnic origin, political opinions, cults,
              religious or philosophical beliefs, sexual behavior, criminal
              records, health data, disability, trade union information, genetic
              data, biometric data, or any data which may affect the data
              subject in the same manner as to be prescribed by the Personal
              Data Protection Committee. In order to operate the Platform, we
              collect, use, and disclose the following types of Personal Data:
            </Paragraph>
            <List>
              <ListItem>
                (a) Identity Data: such as, first name, last name, title, date
                of birth, gender, information to create user profiles (e.g.,
                username, password), and other identifiers;
              </ListItem>
              <ListItem>
                (b) Contact Details: such as, telephone number, email address,
                social network account, and other similar information;
              </ListItem>
              <ListItem>
                (c) Usage Data: such as, login data, browser type and version,
                operating system, time zone and language preference settings,
                approximate geographical information, details of devices the
                User is using (e.g., IP address, name of internet provider,
                device serial number, unique identification number, cookie ID),
                Platform use information (e.g., date of visit, time and length
                of visit, frequency of visits, words input to search for
                information, interactions with features on the Platform);
              </ListItem>
              <ListItem>
                Marketing Data: such as, User's preferences in marketing
                communications, User's survey responses;
              </ListItem>
              <ListItem>
                (e) Interests and Opinions: such as, interests (e.g., interests
                in specific types of content), spending behavior information,
                feedback, comments, recommendations and survey responses;
              </ListItem>
              <ListItem>
                (f) Other Information: such as, information Users provide to the
                Company
              </ListItem>
            </List>
            <Paragraph>
              If you provide Personal Data of others to us, you are responsible
              to:
            </Paragraph>
            <List>
              <ListItem>
                (i) verify the accuracy and completeness of Personal Data
                provided by you and will update us on any changes to the
                provided Personal Data;
              </ListItem>
              <ListItem>
                (ii) obtain consent if required or rely on other legal basis in
                order to allow us to use such Personal Data in accordance to
                this Privacy Policy; and
              </ListItem>
              <ListItem>
                (iii) inform such individual about this Privacy Policy.
              </ListItem>
            </List>
            <SectionTitle>2. HOW WE COLLECT PERSONAL DATA</SectionTitle>
            <Paragraph>
              We collect your Personal Data through the following means:
            </Paragraph>
            <List>
              <ListItem>
                (1) Direct Interactions: Personal Data may be collected directly
                from the Users, by having Users fill in application forms or any
                other forms, provide information and verification to the
                Platform, by email or other direct interactions.
              </ListItem>
              <ListItem>
                (2) Third Parties: The Company may collect the User's Personal
                Data from third party sources, such as publicly available
                sources (e.g., the User's public profile on third party social
                network platforms where the User allows their Personal Data to
                be shared publicly), other Users, government agencies, or other
                publicly available information on the internet.
              </ListItem>
            </List>
            <SectionTitle>3. HOW WE USE PERSONAL DATA</SectionTitle>
            <Paragraph>
              The Company collects, uses or discloses the User's Personal Data
              on the following legal basses: (1) a contractual basis, for our
              initiation or fulfilment of a contract with you; (2) a legal
              obligation; (3) the legitimate interest of ourselves and third
              parties, to be balanced with your own interest and fundamental
              rights and freedoms in relation to the protection of your Personal
              Data; (4) vital interest, for preventing or suppressing a danger
              to a person’s life, body or health; and (5) public interest, for
              the performance of a task carried out in the public interest or
              for the exercise of official actions. The purposes of collection,
              use and disclosure of the User's Personal Data are as follows:
            </Paragraph>
            <List>
              <ListItem>
                (a) Account registration, identification and verification of the
                User's identity, and maintenance of User accounts;
              </ListItem>
              <ListItem>
                (b) Delivering the Platform's services to the Users, including
                but not limited to executing, managing, and processing any
                instructions or orders, and providing support to the Users;
              </ListItem>
              <ListItem>
                (c) Preventing Users from violating the Platform's terms and
                conditions through prohibited use of the Platform, and
                administering and protecting the Platform and other Users from
                such prohibited use by implementing measures such as bans,
                troubleshooting, data analysis, testing, system maintenance,
                support, reporting and hosting of data;
              </ListItem>
              <ListItem>
                (d) User interaction, including User provided reviews, and
                conducting surveys;
              </ListItem>
              <ListItem>
                (e) Providing marketing communications, information, special
                offers, promotional materials about the products and services of
                the Platform, the Company, and/or any business partners;
              </ListItem>
              <ListItem>
                (f) Managing, collecting and transferring payments, fees and
                charges on the Platform that are owed to the Company, and
                ensuring the Company's collection of fees and charges;
              </ListItem>
              <ListItem>
                (g) Displaying the username and other information of the User to
                other Users;
              </ListItem>
              <ListItem>
                (h) Making suggestions and recommendations about goods or
                services;
              </ListItem>
              <ListItem>
                (i) Processing subsidy charge requests by our affiliates;
              </ListItem>
              <ListItem>
                (j) Authenticating and accessing controls and logs where
                applicable, monitoring of system, devices and internet, ensuring
                IT security and IT operations are well maintained, preventing
                and solving crimes, as well as risk management;
              </ListItem>
              <ListItem>
                (k) Ensuring compliance with the Company's internal policies and
                applicable laws, regulations, and regulatory guidelines,
                including but not limited to, liaising and interacting with and
                responding to law enforcement agencies, courts, regulators
                and/or any other government authority;
              </ListItem>
              <ListItem>
                (l) Facilitating financial audits to be performed by an auditor,
                or receiving legal advisory services from legal counsel
                appointed;
              </ListItem>
              <ListItem>
                (m) Transferring and sharing of Personal Data in connection with
                or during business transitions, mergers and acquisitions,
                dissolution, reorganization, or any procedure involving the
                sale, transfer, divestiture, or disclosure of all or a portion
                of our business or assets, or in the event of insolvency,
                bankruptcy, or receivership, or any similar event;
              </ListItem>
              <ListItem>
                (n) Performing our obligations under any agreements to which we
                are a party, e.g. agreements with our business partners,
                vendors, or other asset management companies, or under which we
                are acting as an agent; and
              </ListItem>
            </List>
            <Paragraph>
              Failure to provide us with the required Personal Data may result
              in our inability to perform the contract we have or are trying to
              enter into with you (for example, to provide the User with
              products or services). We would be unable to provide or continue
              to provide you any services, including those on the Platform, if
              the User fails to provide any requested Personal Data that is
              required by law or that is necessary for our provision of the
              services.
            </Paragraph>
            <Paragraph>
              Where consent is required, we will request and obtain your consent
              separately for certain activities pertaining to the collection,
              use or disclosure of your Personal Data.
            </Paragraph>
            <Paragraph>
              We do not intentionally collect, use or disclose any Sensitive
              Personal Data. We will only collect, use, or disclose Sensitive
              Personal Data on the basis of your explicit consent, for
              establishment, defense, compliance or exercise of legal claims,
              for satisfying legal obligations in relation to substantial public
              interest or where it is information that is disclosed to the
              public with the explicit consent from you or where otherwise
              permitted by law.
            </Paragraph>
            <Paragraph>
              If minors, incompetent persons or quasi-incompetent persons
              ("Persons with Limited Capacity") use the Platform, the Company
              will only process their Personal Data provided that verifiable
              consent has been obtained from a parent, guardian, or curator, or
              as otherwise permitted by applicable laws. However, the Company
              does not intend to, and does not knowingly, collect Personal Data
              of Persons with Limited Capacity, and will stop processing such
              Personal Data until consent from the parent, guardian, or curator
              has been obtained or as otherwise permitted by applicable laws.
            </Paragraph>
            <SectionTitle>4. WHOM DO WE SHARE PERSONAL DATA WITH?</SectionTitle>
            <Paragraph>
              The Company may share User's Personal Data with other Users, our
              group of companies, affiliates, business partners, domestic and
              international third-party service providers, agents,
              subcontractors, and other associated organizations, to allow us to
              complete tasks and provide the Platform services (e.g. companies
              of organizations that assist the Platform in processing Personal
              Data, providing cloud storage or any backup database, providing
              platform promotion and marketing activities, etc..) These entities
              are also obligated to follow the security measures surrounding the
              User's Personal Data and to treat it in accordance with applicable
              laws and regulations.
            </Paragraph>
            <Paragraph>
              In some cases, the Company may share the User's Personal Data with
              any government authority, law enforcement agency, court,
              regulator, or other third party where necessary for compliance
              with a legal or regulatory obligation, or otherwise to protect our
              rights, the rights of any third party or individual, personal
              safety, or to detect, prevent, or otherwise address fraud,
              security or safety issues.
            </Paragraph>
            <Paragraph>
              In the event of a corporate transaction, such as a corporate sale,
              transfer, reorganization, merger and acquisition, dissolution, or
              in the event of insolvency, bankruptcy, or receivership, or any
              similar event, the Company may disclose the User's Personal Data
              to another entity.
            </Paragraph>
            <SectionTitle>5. Cross-border Data Transfer</SectionTitle>
            <Paragraph>
              The Company may need to transfer the User's Personal Data to other
              countries, which may have a higher or lower level of data
              protection standards than in Thailand, for the purpose of storing
              data on the servers of an overseas cloud storage provider, and for
              other purpose of mentioned in this Privacy Policy.
            </Paragraph>
            <Paragraph>
              The Company will ensure an adequate degree of protection is
              afforded to the transferred Personal Data, or that the transfer is
              otherwise permitted in accordance with the applicable data
              protection law. When it is necessary to transfer any User's
              Personal Data to a third country with a level of data protection
              standards lower than those of Thailand, the Company may, for
              example, obtain contractual assurances from any third party given
              access to the transferred Personal Data that such data will be
              protected by data protection standards which are equivalent to
              those required in Thailand.
            </Paragraph>
            <SectionTitle>6. Data Retention</SectionTitle>
            <Paragraph>
              A User's Personal Data may be detained for as long as it is
              reasonably necessary to comply with our legal and regulatory
              obligations, and to fulfil the purpose for which it was obtained,
              i.e., the provision of the Platform to our Users.
            </Paragraph>
            <Paragraph>
              It is possible that we may have to retain your Personal Data for a
              longer duration, for certain necessary reasons such as resolving
              disputes, enforcing agreements or other purposes of data retention
              as required by applicable laws.
            </Paragraph>
            <SectionTitle>7. DATA SECURITY MEASURES</SectionTitle>
            <Paragraph>
              We maintain appropriate security measures for Personal Data, which
              cover administrative, technical and physical safeguards in
              relation to access control to protect the confidentiality,
              integrity, and availability of Personal Data against any
              accidental loss or unlawful or unauthorized access, use,
              alteration, correction or disclosure of Personal Data, in
              compliance with the applicable laws. In particular, we have
              implemented measures on controlling access to and use of devices
              for storing and processing Personal Data which are secured and
              suitable for our collection, use and/or disclosure of Personal
              Data. We also have measures on restricting access to Personal Data
              and the use of storage and processing equipment by imposing access
              rights, permission rights to the authorized personnel, and
              relevant duties and responsibilities to prevent unauthorized
              access, disclosure, perception or unlawful duplication of Personal
              Data, or theft of devices used to store and process Personal Data.
              This includes measures on re-examination in relation to access,
              alteration, erasure, or transfer of Personal Data, which are in
              accordance with methods and channels used to collect, use and/or
              disclose Personal Data.
            </Paragraph>

            <SectionTitle>8. DATA SUBJECT RIGHTS</SectionTitle>
            <Paragraph>
              You may request or exercise the following legal rights under the
              conditions and exceptions prescribed by law and our procedures:
            </Paragraph>
            <List>
              <ListItem>
                (a) to withdraw consent for the collection, use, or disclosure
                of your Personal Data that is based on your consent at any time.
                The consent withdrawal will not affect the lawfulness of the
                collection, use and/or disclosure of your Personal Data before
                it was withdrawn. If you withdraw your consent for certain
                Personal Data, we may not be able to provide our services or
                products to you which require such Personal Data.
              </ListItem>
              <ListItem>
                (b) to request access to and obtain a copy of your Personal
                Data, or for the disclosure of the acquisition of your Personal
                Data that was obtained without your consent;
              </ListItem>
              <ListItem>
                (c) to request your Personal Data in a structured, commonly used
                and machine-readable format, to transmit it to other data
                controller, and to request the receipt of the Personal Data in
                such format transmitted by us directly to other data controller
                unless not technically feasible;
              </ListItem>
              <ListItem>
                (d) to object the collection, use, or disclosure of your
                Personal Data in certain circumstances as provided by the
                applicable law;
              </ListItem>
              <ListItem>
                (e) to erase, destroy, or anonymize your Personal Data. Please
                note that we may not always be able to proceed for specific
                legal reasons, which we will inform you of and the reason when
                necessary;
              </ListItem>
              <ListItem>
                (f) to restrict the use of Personal Data in certain
                circumstances as provided by the applicable law;;
              </ListItem>
              <ListItem>
                (g) to have your Personal Data corrected, if it is inaccurate,
                not up to date or misleading;
              </ListItem>
              <ListItem>
                (h) to lodge a complaint to competent regulatory authorities if
                you believe our collection, use or disclosure of your Personal
                Data violates any applicable data protection laws. The Company
                would, however, appreciate the chance to address your concerns
                before approaching the authorities, so please contact us in the
                first instance.
                <Paragraph>
                  If you would like to exercise any of these rights in this
                  section, you may contact the Company at the address and
                  methods specified in the "Contact Us" section. Your request
                  may be reasonably and lawfully declined in certain cases, for
                  example, due to our legal obligations or a court order. The
                  applicable laws may limit your request for exercising any of
                  the above rights, however, we will notify you of our reason if
                  we decline your request under this section.
                </Paragraph>
              </ListItem>
            </List>
            <SectionTitle>9. CHANGES TO PRIVACY POLICY</SectionTitle>
            <Paragraph>
              We may update this Privacy Policy from time to time as our data
              protection practices may change due to changes in law or
              technological developments. Amendments to this Privacy Policy will
              be effective once published by the Company on the Platform.
            </Paragraph>
            <SectionTitle>10. CONTACT US</SectionTitle>
            <Paragraph>
              We are pleased to help with any relevant requests for information,
              suggestions or complaints. You can contact us using the details
              below if you have any questions about the Company's practices and
              activities relating to Personal Data.
            </Paragraph>
            <Paragraph>The Up Rank Company Limited Committee.</Paragraph>
            <Paragraph>
              "333/4 Ratchadaniwet Village Soi. 19 Pracha Uthit Rd. Sam Sen Nok,
              Huai Khwang, Bangkok 10310
            </Paragraph>
          </Container>
        </StyledContainer>
      </StyledWrapper>
    </MainLayout>
  );
};

export default PrivacyPolicy;
